// LearningArticlePage — read a single guide. Portal users: read + mark as read.
// Editors: edit, delete, manage attachments (files + inline images) and see who
// has read the guide.
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Loader2, ArrowLeft, CheckCircle2, Circle, Pencil, Trash2, Upload,
    Download, Paperclip, Copy, Eye, Users, ImageIcon, FileDown,
} from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePageHeaderEffect } from "@/context/PageHeaderContext";
import Markdown from "@/components/Markdown";
import { articleIcon } from "./iconMap";
import ArticleEditorDialog from "@/components/learning/ArticleEditorDialog";

const BRAND = "var(--brand-accent, #C75A1B)";
const EDITOR_ROLES = new Set(["director", "administrator", "consultant"]);

function attUrl(articleId, attId) {
    return `${API}/learning/attachments/${articleId}/${attId}`;
}
function fmtBytes(b) {
    if (!b) return "";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export default function LearningArticlePage() {
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const isEditor = user && EDITOR_ROLES.has(user.role);
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [marking, setMarking] = useState(false);
    const [reads, setReads] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const fileRef = useRef(null);

    usePageHeaderEffect({ title: "Help & Guides", subtitle: "Guide" }, []);

    const load = useCallback(() => {
        setLoading(true);
        api.get(`/learning/articles/${id}`)
            .then((r) => setArticle(r.data))
            .catch((e) => {
                toast.error(formatApiError(e.response?.data?.detail) || "Could not open this guide");
                navigate("/app/learning");
            })
            .finally(() => setLoading(false));
    }, [id, navigate]);
    useEffect(load, [load]);

    const toggleRead = async () => {
        if (!article) return;
        setMarking(true);
        try {
            if (article.read) {
                await api.delete(`/learning/articles/${id}/read`);
                setArticle((a) => ({ ...a, read: false }));
            } else {
                await api.post(`/learning/articles/${id}/read`);
                setArticle((a) => ({ ...a, read: true }));
                toast.success("Marked as read.");
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not update");
        } finally {
            setMarking(false);
        }
    };

    const remove = async () => {
        if (!window.confirm("Delete this guide? This cannot be undone.")) return;
        try {
            await api.delete(`/learning/articles/${id}`);
            toast.success("Guide deleted.");
            navigate("/app/learning");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not delete");
        }
    };

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const r = await api.post(`/learning/articles/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setArticle(r.data ? { ...r.data, read: article.read, is_editor: article.is_editor, read_count: article.read_count } : article);
            toast.success("Attachment added.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const deleteAttachment = async (attId) => {
        try {
            const r = await api.delete(`/learning/articles/${id}/attachments/${attId}`);
            setArticle((a) => ({ ...a, attachments: r.data.attachments }));
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not remove");
        }
    };

    const downloadFile = async (att) => {
        try {
            const resp = await api.get(`/learning/attachments/${id}/${att.id}`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([resp.data], { type: resp.headers["content-type"] || att.mime_type }));
            const a = document.createElement("a");
            a.href = url; a.download = att.filename;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Download failed");
        }
    };

    const downloadArticlePdf = async () => {
        setDownloadingPdf(true);
        try {
            const resp = await api.get(`/learning/articles/${id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url; a.download = `${article.slug || "guide"}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Could not generate the PDF");
        } finally {
            setDownloadingPdf(false);
        }
    };

    const copyImageMarkdown = (att) => {
        const md = `![${att.filename}](${attUrl(id, att.id)})`;
        navigator.clipboard?.writeText(md);
        toast.success("Image Markdown copied — paste it into the guide content.");
    };

    const loadReads = async () => {
        try {
            const r = await api.get(`/learning/articles/${id}/reads`);
            setReads(r.data || []);
        } catch (e) {
            toast.error("Could not load readers");
        }
    };

    if (loading || !article) {
        return <div className="text-center py-16 text-[#9A968F]"><Loader2 size={18} className="inline animate-spin mr-2" />Loading…</div>;
    }

    const Icon = articleIcon(article.icon);
    const images = (article.attachments || []).filter((a) => a.kind === "image");
    const files = (article.attachments || []).filter((a) => a.kind !== "image");

    return (
        <div data-testid="learning-article-page" className="max-w-3xl space-y-5">
            <button onClick={() => navigate("/app/learning")} data-testid="learning-back"
                className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold" style={{ color: BRAND }}>
                <ArrowLeft size={14} /> All guides
            </button>

            <div className="bg-white border rounded-[16px] overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                {/* Header */}
                <div className="px-6 pt-6 pb-4 flex items-start gap-4 border-b" style={{ borderColor: "#F0EDE8" }}>
                    <div className="w-12 h-12 rounded-[12px] grid place-items-center flex-shrink-0" style={{ background: "#FBEFE5", color: BRAND }}>
                        <Icon size={22} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-[10.5px] font-bold uppercase tracking-wider" style={{ color: "#9A968F" }}>{article.category}</div>
                        <h1 className="text-[22px] font-bold text-[#1F1F1F] leading-tight mt-0.5">{article.title}</h1>
                        {article.summary && <p className="text-[13px] mt-1.5" style={{ color: "#6F6A64" }}>{article.summary}</p>}
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0">
                        <button data-testid="learning-download-pdf" onClick={downloadArticlePdf} disabled={downloadingPdf} title="Download as PDF"
                            className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg border text-[12px] font-semibold hover:bg-[#F6F4F0] disabled:opacity-60"
                            style={{ borderColor: "#E5E0DA", color: "#6F6A64" }}>
                            {downloadingPdf ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />} PDF
                        </button>
                        {isEditor && (
                            <>
                            <button data-testid="learning-edit" onClick={() => setEditing(true)} title="Edit"
                                className="w-8 h-8 grid place-items-center rounded-lg border hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA", color: "#6F6A64" }}><Pencil size={14} /></button>
                            <button data-testid="learning-delete" onClick={remove} title="Delete"
                                className="w-8 h-8 grid place-items-center rounded-lg border hover:bg-[#FBE8E5]" style={{ borderColor: "#E5E0DA", color: "#B33A2E" }}><Trash2 size={14} /></button>
                            </>
                        )}
                    </div>
                </div>

                {/* Body */}
                <div className="px-6 py-5">
                    <Markdown text={article.body} />
                </div>

                {/* Downloadable files */}
                {files.length > 0 && (
                    <div className="px-6 pb-5">
                        <h3 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-2 flex items-center gap-1.5"><Paperclip size={12} /> Downloads</h3>
                        <div className="space-y-1.5">
                            {files.map((att) => (
                                <div key={att.id} data-testid={`learning-file-${att.id}`}
                                    className="flex items-center gap-2.5 px-3 py-2 rounded-lg border" style={{ borderColor: "#E5E0DA" }}>
                                    <Paperclip size={14} style={{ color: "#9A968F" }} />
                                    <span className="text-[13px] font-medium text-[#1F1F1F] flex-1 truncate">{att.filename}</span>
                                    <span className="text-[11px]" style={{ color: "#9A968F" }}>{fmtBytes(att.size)}</span>
                                    <button onClick={() => downloadFile(att)} className="inline-flex items-center gap-1 text-[12px] text-[#2A5DB0] hover:underline"><Download size={12} /> Download</button>
                                    {isEditor && <button onClick={() => deleteAttachment(att.id)} className="text-[#B33A2E] hover:opacity-70"><Trash2 size={13} /></button>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Mark as read footer */}
                <div className="px-6 py-4 border-t flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: "#F0EDE8", background: "#FCFBFA" }}>
                    <p className="text-[12px]" style={{ color: "#6F6A64" }}>
                        {article.read ? "You've read this guide." : "Let us know you've read this guide."}
                    </p>
                    <button data-testid="learning-mark-read" onClick={toggleRead} disabled={marking}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[9px] text-[12.5px] font-semibold transition-colors disabled:opacity-60"
                        style={article.read
                            ? { background: "#E8F1E6", color: "#3F7D3A" }
                            : { background: BRAND, color: "#fff" }}>
                        {marking ? <Loader2 size={14} className="animate-spin" /> : article.read ? <CheckCircle2 size={15} /> : <Circle size={13} />}
                        {article.read ? "Read — mark as unread" : "Mark as read"}
                    </button>
                </div>
            </div>

            {/* Editor tools: image attachments + read receipts */}
            {isEditor && (
                <div className="bg-white border rounded-[14px] p-5 space-y-4" style={{ borderColor: "#E5E0DA" }}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <h3 className="text-[12px] font-bold text-[#1F1F1F] flex items-center gap-1.5"><ImageIcon size={14} /> Attachments &amp; screenshots</h3>
                        <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold border cursor-pointer hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>
                            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload file / image
                            <input ref={fileRef} data-testid="learning-upload-input" type="file" className="hidden" onChange={onUpload} disabled={uploading} />
                        </label>
                    </div>
                    <p className="text-[11.5px]" style={{ color: "#9A968F" }}>
                        Upload a screenshot, then click <b>Copy image</b> and paste the Markdown into the guide content to place it inline. Non-image files appear in the Downloads section.
                    </p>
                    {images.length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {images.map((att) => (
                                <div key={att.id} data-testid={`learning-image-${att.id}`} className="border rounded-lg overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                                    <img src={attUrl(id, att.id)} alt={att.filename} className="w-full h-24 object-cover" />
                                    <div className="flex items-center justify-between px-2 py-1.5 gap-1">
                                        <button onClick={() => copyImageMarkdown(att)} className="inline-flex items-center gap-1 text-[11px] text-[#2A5DB0] hover:underline"><Copy size={11} /> Copy image</button>
                                        <button onClick={() => deleteAttachment(att.id)} className="text-[#B33A2E] hover:opacity-70"><Trash2 size={12} /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="border-t pt-4" style={{ borderColor: "#F0EDE8" }}>
                        <div className="flex items-center justify-between gap-2">
                            <h3 className="text-[12px] font-bold text-[#1F1F1F] flex items-center gap-1.5"><Users size={14} /> Read receipts <span className="font-normal" style={{ color: "#9A968F" }}>· {article.read_count} total</span></h3>
                            <button data-testid="learning-load-reads" onClick={loadReads} className="inline-flex items-center gap-1 text-[12px] text-[#2A5DB0] hover:underline"><Eye size={12} /> {reads === null ? "See who's read it" : "Refresh"}</button>
                        </div>
                        {reads !== null && (
                            reads.length === 0
                                ? <p className="text-[12px] mt-2" style={{ color: "#9A968F" }}>No one has marked this as read yet.</p>
                                : <div className="mt-2 border rounded-lg divide-y" style={{ borderColor: "#E5E0DA" }}>
                                    {reads.map((r) => (
                                        <div key={r.user_id} className="flex items-center gap-2 px-3 py-1.5 text-[12.5px]">
                                            <CheckCircle2 size={13} style={{ color: "#3F7D3A" }} />
                                            <span className="font-medium text-[#1F1F1F]">{r.user_name || r.user_id}</span>
                                            <span className="text-[#9A968F]">{r.user_role}</span>
                                            <span className="ml-auto text-[11px] text-[#9A968F]">{(r.read_at || "").slice(0, 10)}</span>
                                        </div>
                                    ))}
                                </div>
                        )}
                    </div>
                </div>
            )}

            {editing && (
                <ArticleEditorDialog article={article} onClose={() => setEditing(false)}
                    onSaved={() => { setEditing(false); load(); }} />
            )}
        </div>
    );
}
